'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { IdentityInfoSchemaType } from '@/types/portal-api.generated'
import { isFeatureFlagEnabledFromJson, useLaunchDarkly } from '@/providers/LaunchDarkly'
import {
  CLIENT_PORTAL_PCP_INFORMATION_FLAG_KEY,
  parsePcpInformationFlag,
} from '@/flows/hooks/usePCPInformationFlags'
import { DefaultRoleSyncStrategy } from '@/services/shared/auth/roleSyncStrategy'
import {
  fetchTrustedIdentityScopes,
  getCachedTrustedIdentityScopes,
  setCachedTrustedIdentityScopesFromIdentity,
} from '@/utilities/auth/trustedIdentityScopes'
import { getIdentity } from '@/services/shared/authRepository'
import { USER_IDENTITY_SET_EVENT } from '@/utilities/client/userIdentityEvent'
import {
  findIdentityScopeByNormalizedPartnerId,
  getRolesFromScopes,
  hasNonPhiRole,
  type IdentityScopes,
} from '@/utilities/auth/identityScopeRoles'
import { errorAnalyticsPayload } from '@/utilities/analytics/errorAnalytics'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { normalizeNullableClientId } from '@/utilities/auth/authHelper'
import {
  areClientFeatureFlagContextsEqual,
  type ClientFeatureFlagContext,
} from '@/utilities/client/clientPortalContext'

export const KIT_REGISTRATION_ACCESS_FLAG = 'client-portal-kit-registration-access'
export const CLIENT_PORTAL_ELIGIBILITY_QUESTIONNAIRE_FLAG_KEY =
  'client-portal-eligibility-questionnaire'

type RoleAccessState = {
  hasAllowedAdminRole: boolean
  isPhiUser: boolean
  isRoleCheckReady: boolean
}

const NOT_READY_ACCESS_STATE: RoleAccessState = {
  hasAllowedAdminRole: false,
  isPhiUser: false,
  isRoleCheckReady: false,
}

function getSelectedClientScopes(
  scopes: IdentityScopes | null | undefined,
  selectedClientId: string | null | undefined,
): IdentityScopes | undefined {
  const normalizedClientId = normalizeNullableClientId(selectedClientId)
  const selectedScope = findIdentityScopeByNormalizedPartnerId(scopes, normalizedClientId)
  if (!normalizedClientId || !selectedScope) return undefined

  return { [normalizedClientId]: selectedScope } as IdentityScopes
}

export function isKitRegistrationFeatureFlagEnabled(value: unknown): boolean {
  return typeof value === 'string' && value.trim().toLowerCase() === 'enabled'
}

export function resolveKitRegistrationRoleAccess(
  scopes: IdentityScopes | null | undefined,
  selectedClientId?: string | null,
): RoleAccessState {
  const roleSync = new DefaultRoleSyncStrategy()
  const isAshAdmin = roleSync.isAshAdmin(scopes ?? undefined)

  if (isAshAdmin) {
    const roles = getRolesFromScopes(scopes)

    return {
      hasAllowedAdminRole: true,
      isPhiUser: roles.length > 0 && !hasNonPhiRole(roles),
      isRoleCheckReady: true,
    }
  }

  const selectedScopes = getSelectedClientScopes(scopes, selectedClientId)
  const selectedRoles = getRolesFromScopes(selectedScopes)

  return {
    hasAllowedAdminRole: roleSync.isOrgAdmin(selectedScopes),
    isPhiUser: selectedRoles.length > 0 && !hasNonPhiRole(selectedRoles),
    isRoleCheckReady: true,
  }
}

export function useKitRegistrationAccess(selectedClientId?: string | null): {
  canAccessKitRegistration: boolean
  hasAllowedAdminRole: boolean
  isKitRegistrationAccessReady: boolean
  isKitRegistrationFeatureEnabled: boolean
  isPcpStepEnabled: boolean
  isEligibilityQuestionnaireFlagEnabled: boolean
  isPhiUser: boolean
} {
  const cachedScopes = getCachedTrustedIdentityScopes()
  const [roleAccess, setRoleAccess] = useState<RoleAccessState>(() =>
    cachedScopes
      ? resolveKitRegistrationRoleAccess(cachedScopes, selectedClientId)
      : NOT_READY_ACCESS_STATE,
  )
  const latestRequestRef = useRef(0)
  const isMountedRef = useRef(true)
  const {
    flags,
    isLoading: areFeatureFlagsLoading,
    hasFetched: haveFeatureFlagsFetched,
    error: featureFlagsError,
    clientContext: loadedFeatureFlagContext,
  } = useLaunchDarkly()

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const applyScopes = useCallback(
    (scopes: IdentityScopes) => {
      setRoleAccess(resolveKitRegistrationRoleAccess(scopes, selectedClientId))
    },
    [selectedClientId],
  )

  const evaluateRoleAccess = useCallback(
    (options?: { identity?: IdentityInfoSchemaType }) => {
      const requestId = ++latestRequestRef.current
      const providedIdentity = options?.identity

      if (providedIdentity !== undefined) {
        applyScopes(setCachedTrustedIdentityScopesFromIdentity(providedIdentity))
        return
      }

      const cachedScopes = getCachedTrustedIdentityScopes()
      if (cachedScopes) {
        applyScopes(cachedScopes)
        return
      }

      setRoleAccess(NOT_READY_ACCESS_STATE)

      void fetchTrustedIdentityScopes(getIdentity)
        .then((scopes) => {
          if (!isMountedRef.current || requestId !== latestRequestRef.current) {
            return
          }
          applyScopes(scopes)
        })
        .catch((error) => {
          if (!isMountedRef.current || requestId !== latestRequestRef.current) {
            return
          }
          console.error('Error checking kit registration access:', error)
          trackEvent('client:kit_registration:access_check_fail', {
            selectedClientId: selectedClientId ?? null,
            ...errorAnalyticsPayload(error),
          })
          setRoleAccess({
            hasAllowedAdminRole: false,
            isPhiUser: false,
            isRoleCheckReady: true,
          })
        })
    },
    [applyScopes, selectedClientId],
  )

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- state update required inside this async/side-effect; cannot be derived
    evaluateRoleAccess()
  }, [evaluateRoleAccess])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleUserIdentitySet = (event: Event) => {
      const identity = (event as CustomEvent<{ identity?: IdentityInfoSchemaType } | undefined>)
        .detail?.identity
      evaluateRoleAccess(identity === undefined ? undefined : { identity })
    }

    window.addEventListener(USER_IDENTITY_SET_EVENT, handleUserIdentitySet)
    return () => window.removeEventListener(USER_IDENTITY_SET_EVENT, handleUserIdentitySet)
  }, [evaluateRoleAccess])

  return useMemo(() => {
    const normalizedClientId = normalizeNullableClientId(selectedClientId)
    const expectedFeatureFlagContext: ClientFeatureFlagContext = normalizedClientId
      ? { kind: 'partner', partnerId: normalizedClientId }
      : { kind: 'none' }
    const isFeatureFlagPartnerAligned = areClientFeatureFlagContextsEqual(
      loadedFeatureFlagContext,
      expectedFeatureFlagContext,
    )
    const isFeatureFlagCheckReady =
      isFeatureFlagPartnerAligned &&
      !areFeatureFlagsLoading &&
      (haveFeatureFlagsFetched || featureFlagsError !== null)
    const isKitRegistrationFeatureEnabled = isKitRegistrationFeatureFlagEnabled(
      flags[KIT_REGISTRATION_ACCESS_FLAG],
    )
    const isKitRegistrationAccessReady = roleAccess.isRoleCheckReady && isFeatureFlagCheckReady
    const isPcpStepEnabled =
      isFeatureFlagCheckReady &&
      featureFlagsError === null &&
      parsePcpInformationFlag(flags[CLIENT_PORTAL_PCP_INFORMATION_FLAG_KEY]).enabled === true
    const isEligibilityQuestionnaireFlagEnabled =
      isFeatureFlagCheckReady &&
      featureFlagsError === null &&
      isFeatureFlagEnabledFromJson(flags[CLIENT_PORTAL_ELIGIBILITY_QUESTIONNAIRE_FLAG_KEY])
    const canAccessKitRegistration =
      isKitRegistrationAccessReady &&
      isKitRegistrationFeatureEnabled &&
      roleAccess.hasAllowedAdminRole &&
      roleAccess.isPhiUser

    return {
      canAccessKitRegistration,
      hasAllowedAdminRole: roleAccess.hasAllowedAdminRole,
      isKitRegistrationAccessReady,
      isKitRegistrationFeatureEnabled,
      isPcpStepEnabled,
      isEligibilityQuestionnaireFlagEnabled,
      isPhiUser: roleAccess.isPhiUser,
    }
  }, [
    areFeatureFlagsLoading,
    featureFlagsError,
    flags,
    haveFeatureFlagsFetched,
    loadedFeatureFlagContext,
    roleAccess,
    selectedClientId,
  ])
}
